// Full-screen skeleton shown during a tenant switch.
//
// A tenant switch mints a fresh JWT then hard-reloads (the reload path is the
// battle-tested source-of-truth — see Dashboard.switchTo). Two rough edges
// that this overlay smooths:
//   1. For up to ~3s before the reload, the OLD tenant's dashboard is still on
//      screen ("stale"). The overlay covers it the instant you tap switch.
//   2. The reload itself flashes. The same overlay re-renders on boot (driven
//      by a sessionStorage flag) so the transition reads as one continuous
//      loading state rather than old-data → white → new-data.
//
// Pure presentation — a dashboard-shaped shimmer + a "Switching…" line.
import clsx from 'clsx'
import { Skeleton } from './Skeleton'

export default function TenantSwitchOverlay({ name, label, closing = false }: {
  name?:    string | null
  /** Overrides the default "Switching to <name>…" line (e.g. "Updating…"). */
  label?:   string | null
  closing?: boolean
}) {
  return (
    <div
      className={clsx(
        'fixed inset-0 z-overlay surface-sunk flex flex-col transition-opacity duration-300',
        closing ? 'opacity-0' : 'opacity-100',
      )}
      // Own both iOS safe-areas so the skeleton doesn't sit under the notch /
      // home-indicator on a standalone PWA.
      style={{
        paddingTop:    'max(env(safe-area-inset-top), 0px)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      aria-busy="true"
      aria-label={label || (name ? `Switching to ${name}` : 'Switching tenant')}
    >
      {/* header skeleton */}
      <div className="px-4 py-3 border-b hairline flex items-center gap-3 shrink-0">
        <Skeleton className="h-9 w-9 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3 w-32 rounded" />
          <Skeleton className="h-2.5 w-20 rounded opacity-70" />
        </div>
        <Skeleton className="h-8 w-24 rounded-xl" />
      </div>

      {/* body skeleton */}
      <div className="flex-1 overflow-hidden p-4 space-y-4 max-w-3xl mx-auto w-full">
        <div role="status" aria-live="polite"
          className="flex items-center justify-center gap-2 text-sm text-fg-muted py-1">
          <span className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          {label
            ? label
            : <>Switching{name ? <> to <span className="text-fg-secondary font-medium">{name}</span></> : ' tenant'}…</>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl opacity-80" />
          ))}
        </div>
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-xl opacity-60" />
          ))}
        </div>
      </div>
    </div>
  )
}
